///
/// EarthBrowser
///
/// Created by Matt Giger on 10/28/2026
/// Copyright (c) 2026 Geodata Labs
///

import {
	ContinuousGestureRecognizer,
	GestureState,
	type GestureEventData,
	type GestureRecognizerConfig,
	type TouchPoint
} from "./GestureRecognizer"

/**
 * What a two-finger gesture turned out to be, decided once at begin:
 * - tilt: both fingers travelling together, mostly vertically (two-finger
 *   drag up/down), the phone-map tilt gesture
 * - transform: everything else - pinch, twist and two-finger pan, which
 *   then all run at once about the centroid
 */
export type TwoFingerMode = "tilt" | "transform"

export interface TwoFingerGestureEventData extends GestureEventData {
	mode: TwoFingerMode
	/** Centroid when the gesture began (canvas px) */
	startX: number
	startY: number
	/** Centroid travel since the gesture began */
	translationX: number
	translationY: number
	/** Span ratio since the pinch component engaged; exactly 1 before that */
	scale: number
	/** Twist since the twist component engaged (radians, + counter-clockwise on screen); exactly 0 before that */
	rotation: number
	/** Finger span when the gesture began (px) */
	span: number
	pointerType: string
}

export interface TwoFingerGestureConfig extends GestureRecognizerConfig {
	/** Motion (centroid travel, span change or twist arc, px) before the gesture begins and is classified (default 10) */
	slop?: number
	/** |scale - 1| at which the pinch component engages (default 0.03) */
	scaleEngage?: number
	/** |rotation| (radians) at which the twist component engages (default 2.5 deg) */
	rotationEngage?: number
	/** How much the centroid's vertical travel must exceed its horizontal travel to read as tilt (default 1.5) */
	tiltAxisRatio?: number
}

/**
 * One recognizer for everything two fingers do, replacing the race between
 * separate pinch / twist / swipe recognizers (which made a sloppy pinch a
 * lottery). It begins after a little motion of any kind, classifies the
 * gesture once (tilt or transform), then streams the full two-finger state.
 *
 * In transform mode the pinch and twist components engage individually
 * with hysteresis, re-based at the moment they engage so nothing jumps: a
 * pinch with a few degrees of incidental twist stays a pure pinch until the
 * twist is deliberate, and a twist stays a twist while the span drifts a few
 * percent. Once engaged, a component stays engaged for the gesture.
 */
export class TwoFingerGestureRecognizer extends ContinuousGestureRecognizer<TwoFingerGestureEventData> {
	private slop: number
	private scaleEngage: number
	private rotationEngage: number
	private tiltAxisRatio: number

	private mode: TwoFingerMode = "transform"
	private startCentroid?: { x: number, y: number }
	private startSpan = 0
	private startAngle = 0
	private pointerType = "touch"

	// Component engagement (transform mode)
	private scaleEngaged = false
	private scaleBase = 1
	private rotationEngaged = false
	private rotationBase = 0
	private lastScale = 1
	private lastRotation = 0
	// Span / twist deviation at the previous evaluation. Each finger's move
	// arrives as its own event, so between the two the span transiently
	// changes by one finger's step even when the fingers are moving
	// together; thresholds are judged on the deviation that persists across
	// consecutive evaluations, which that alternation collapses to zero
	private prevSpanChange = 0
	private prevTwistArc = 0

	constructor(config: TwoFingerGestureConfig = {}) {
		super({ ...config, minimumTouches: 2, maximumTouches: 2 })
		this.slop = config.slop ?? 10
		this.scaleEngage = config.scaleEngage ?? 0.03
		this.rotationEngage = config.rotationEngage ?? 2.5 * Math.PI / 180
		this.tiltAxisRatio = config.tiltAxisRatio ?? 1.5
	}

	get gestureType(): string {
		return "pinch"
	}

	handleTouchStart(touches: TouchPoint[]): void {
		if (!this._enabled) return
		touches.forEach(t => this.touches.set(t.id, t))

		if (this.touchCount > 2) {
			if (this.isActive()) {
				this.cancel()
			} else {
				this.fail()
			}
			return
		}
		if (this.touchCount === 2) {
			this.captureStart()
		}
	}

	/** Start geometry for the two fingers now down */
	private captureStart(): void {
		const { span, angle } = this.spanAndAngle()
		this.startCentroid = this.getCentroid()
		this.startSpan = span
		this.startAngle = angle
		this.scaleEngaged = false
		this.rotationEngaged = false
		this.scaleBase = 1
		this.rotationBase = 0
		this.lastScale = 1
		this.lastRotation = 0
		this.prevSpanChange = 0
		this.prevTwistArc = 0
		const first = this.touches.values().next().value as TouchPoint | undefined
		this.pointerType = first?.pointerType ?? "touch"
	}

	handleTouchMove(touches: TouchPoint[]): void {
		if (!this._enabled || this.isTerminal()) return

		// Adopt touches the arena sends that we lost to a mid-session reset
		touches.forEach(t => {
			const existing = this.touches.get(t.id)
			if (existing) {
				existing.x = t.x
				existing.y = t.y
			} else {
				this.touches.set(t.id, t)
			}
		})
		if (this.touchCount !== 2) return
		if (!this.startCentroid || this.startSpan === 0) {
			this.captureStart()
			return
		}

		const { span, angle } = this.spanAndAngle()
		const centroid = this.getCentroid()
		const dx = centroid.x - this.startCentroid.x
		const dy = centroid.y - this.startCentroid.y
		const travel = Math.hypot(dx, dy)
		const rawScale = span / this.startSpan
		const rawRotation = TwoFingerGestureRecognizer.normalizeAngle(angle - this.startAngle)

		// Deviations that have persisted across two evaluations (see prev*)
		const spanChange = Math.abs(span - this.startSpan)
		const twistArc = Math.abs(rawRotation) * this.startSpan / 2
		const stableSpan = Math.min(spanChange, this.prevSpanChange)
		const stableTwist = Math.min(twistArc, this.prevTwistArc)
		this.prevSpanChange = spanChange
		this.prevTwistArc = twistArc
		const pinching = stableSpan / this.startSpan >= this.scaleEngage
		const twisting = stableTwist * 2 / this.startSpan >= this.rotationEngage

		if (!this.isActive()) {
			const travelling = travel >= this.slop
			if (!travelling && !pinching && !twisting) return

			// Classify once: fingers moving together and mostly vertically,
			// with the common-mode travel dominating span change and twist,
			// is the tilt swipe; anything else is a transform
			const vertical = Math.abs(dy) > Math.abs(dx) * this.tiltAxisRatio
			this.mode = travelling && vertical && travel > stableSpan && travel > stableTwist
				? "tilt"
				: "transform"
			// Components already past threshold at begin start from identity
			// (nothing to re-base against: the pinch's first few percent are
			// the gesture, not noise)
			if (this.mode === "transform") {
				this.scaleEngaged = pinching
				this.rotationEngaged = twisting
			}
			this.updateEngagement(rawScale, rawRotation, pinching, twisting)
			this.begin(this.createEventData(GestureState.Began))
			return
		}

		this.updateEngagement(rawScale, rawRotation, pinching, twisting)
		this.change(this.createEventData(GestureState.Changed))
	}

	/**
	 * Engage the pinch / twist components as their persisted deviation passes
	 * threshold. A component engaging mid-gesture is re-based at that moment
	 * so its reported value starts from identity instead of jumping by the
	 * threshold; once engaged it stays engaged
	 */
	private updateEngagement(rawScale: number, rawRotation: number, pinching: boolean, twisting: boolean): void {
		if (this.mode !== "transform") {
			this.lastScale = 1
			this.lastRotation = 0
			return
		}
		if (!this.scaleEngaged && pinching) {
			this.scaleEngaged = true
			this.scaleBase = rawScale
		}
		if (!this.rotationEngaged && twisting) {
			this.rotationEngaged = true
			this.rotationBase = rawRotation
		}
		this.lastScale = this.scaleEngaged ? rawScale / this.scaleBase : 1
		this.lastRotation = this.rotationEngaged
			? TwoFingerGestureRecognizer.normalizeAngle(rawRotation - this.rotationBase)
			: 0
	}

	handleTouchEnd(touches: TouchPoint[]): void {
		if (!this._enabled || this.isTerminal()) return

		if (this.isActive()) {
			// Build the Ended event while both fingers are still known
			const data = this.createEventData(GestureState.Ended)
			touches.forEach(t => this.touches.delete(t.id))
			this.end(data)
			return
		}
		touches.forEach(t => this.touches.delete(t.id))
		if (this.touchCount < 2) {
			this.fail()
		}
	}

	handleTouchCancel(): void {
		this.cancel()
	}

	private spanAndAngle(): { span: number, angle: number } {
		const [a, b] = this.getTouchArray()
		if (!a || !b) return { span: 0, angle: 0 }
		const dx = b.x - a.x
		const dy = b.y - a.y
		return { span: Math.hypot(dx, dy), angle: Math.atan2(dy, dx) }
	}

	private static normalizeAngle(angle: number): number {
		let a = angle
		while (a > Math.PI) a -= 2 * Math.PI
		while (a < -Math.PI) a += 2 * Math.PI
		return a
	}

	protected createEventData(state: GestureState): TwoFingerGestureEventData {
		const start = this.startCentroid ?? { x: 0, y: 0 }
		const centroid = this.touchCount > 0 ? this.getCentroid() : start
		return {
			type: "pinch",
			state,
			timestamp: performance.now(),
			x: centroid.x,
			y: centroid.y,
			ownerHits: this.getOwnerHits(),
			mode: this.mode,
			startX: start.x,
			startY: start.y,
			translationX: centroid.x - start.x,
			translationY: centroid.y - start.y,
			scale: this.lastScale,
			rotation: this.lastRotation,
			span: this.startSpan,
			pointerType: this.pointerType
		}
	}

	override reset(): void {
		super.reset()
		this.mode = "transform"
		this.startCentroid = undefined
		this.startSpan = 0
		this.startAngle = 0
		this.scaleEngaged = false
		this.rotationEngaged = false
		this.scaleBase = 1
		this.rotationBase = 0
		this.lastScale = 1
		this.lastRotation = 0
		this.prevSpanChange = 0
		this.prevTwistArc = 0
	}
}
